import { z } from "zod"

const NUMBER_TYPE_ERROR_MESSAGE = "is required"

// Helper to reject NaN values which can occur when valueAsNumber parses empty/invalid input
const nanToUndefined = (val: unknown) => (typeof val === "number" && Number.isNaN(val) ? undefined : val)

export const shippingCostEstimatorFormStateSchema = z
  .object({
    baseMarginPercentage: z.preprocess(nanToUndefined, z.number({ error: NUMBER_TYPE_ERROR_MESSAGE }).int().positive()),
    allowedCarriers: z.enum(["exclude_economy", "no_restrictions"]),
    shippingKit: z.enum(["corrugated_box", "padded_mailer", "no_additional_packaging", "custom_packaging"]),
    customPackagingWeight: z.number().nonnegative().optional(),
    destinations: z
      .array(
        z.object({
          key: z.unknown().optional(),
          alpha2: z.string("required"),
          zoning: z.enum(["commercial", "residential"]),
          quantity: z.preprocess(nanToUndefined, z.number({ error: NUMBER_TYPE_ERROR_MESSAGE }).int().min(1)),
          carrier: z.string().optional(),
          service: z.string().optional(),
        })
      )
      .min(1),
    packageDimension: z
      .object({
        weight: z.preprocess(nanToUndefined, z.number({ error: NUMBER_TYPE_ERROR_MESSAGE }).positive()).optional(),
        length: z.preprocess(nanToUndefined, z.number({ error: NUMBER_TYPE_ERROR_MESSAGE }).positive()).optional(),
        width: z.preprocess(nanToUndefined, z.number({ error: NUMBER_TYPE_ERROR_MESSAGE }).positive()).optional(),
        height: z.preprocess(nanToUndefined, z.number({ error: NUMBER_TYPE_ERROR_MESSAGE }).positive()).optional(),
      })
      .optional(),
    variants: z
      .array(
        z.object({
          id: z.number().int().positive(),
          quantity: z.preprocess(nanToUndefined, z.number({ error: NUMBER_TYPE_ERROR_MESSAGE }).int().min(1)),
          productId: z.unknown().optional(),
          productName: z.unknown().optional(),
          unitWeightOz: z.number().positive(),
          key: z.unknown().optional(),
        })
      )
      .min(1),
    timeWindow: z.enum(["most_economical_rate", "by_certain_date"]),
    byDays: z.preprocess(nanToUndefined, z.number({ error: NUMBER_TYPE_ERROR_MESSAGE }).int().min(1)).optional(),
  })
  .superRefine((data, ctx) => {
    // Conditional validation: customPackagingWeight required and must be positive when shippingKit is custom_packaging
    if (data.shippingKit === "custom_packaging" && (!data.customPackagingWeight || data.customPackagingWeight <= 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["customPackagingWeight"],
        message: "customPackagingWeight is required and must be positive",
      })
    }

    // Conditional validation: byDays required when timeWindow is by_certain_date
    if (data.timeWindow === "by_certain_date" && !data.byDays) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["byDays"],
        message: "byDays is required",
      })
    }
  })
